import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Header, Icon, Text } from '@components'
import { Theme } from '@theme'
import { Contact } from '@hooks'
import { useLanguage } from '@context'
import ContactsList from '../components/ContactsList'

// Mobile-only screen shown before the note editor. Mirrors the desktop
// "select a contact on the left, then write the note on the right" flow:
// the user picks a contact (private / pro / ghost) — or chooses "personal
// note" — and is then taken to EditNote with that contact pre-filled.
export const SelectNoteRecipient: React.FC = () => {
  const navigation = useNavigation<any>()
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  const goToEditNote = (params?: { contactId: string; isGhost: boolean }) => {
    navigation.navigate('EditNote', params)
  }

  const handleSelectContact = (contact: Contact) => {
    goToEditNote({ contactId: contact.id, isGhost: !!contact.isGhost })
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <Header
        title={t('notesForm.selectContactTitle')}
        iconLeft="arrowBack"
        onPressLeft={() => navigation.goBack()}
      />

      {/* Rendered as the first list row (not a floating CTA): a filled
          "you" badge mirrors the contact avatars below, and a trailing
          chevron marks it as a navigation choice — distinct from the
          checkbox-style selection used for contacts. */}
      <TouchableOpacity style={styles.personalNoteRow} onPress={() => goToEditNote()}>
        <View style={styles.personalNoteBadge}>
          <Icon name="edit" size={theme.iconSize.medium} color={theme.colors.white} />
        </View>
        <View style={styles.personalNoteTextBlock}>
          <Text type="bodyBold" style={styles.personalNoteTitle}>
            {t('notesForm.personalNote')}
          </Text>
          <Text type="bodySmall" style={styles.personalNoteSubtitle}>
            {t('notesForm.personalNoteSubtitle')}
          </Text>
        </View>
        <Icon name="arrowRight" size={16} color={theme.colors.greyBlue} />
      </TouchableOpacity>

      {/* ContactsList renders edge-to-edge; inset it to the same horizontal
          margin as the "Note personnelle" button and the header. */}
      <View style={styles.listContainer}>
        <ContactsList
          multiple={false}
          includeGhost
          title={t('notesForm.addContact')}
          selectedContactIds={[]}
          setSelectedContactIds={() => {}}
          onSelectContact={handleSelectContact}
        />
      </View>
    </SafeAreaView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.white,
    },
    personalNoteRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey20,
    },
    personalNoteBadge: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: theme.spacing.md,
    },
    personalNoteTextBlock: {
      flex: 1,
    },
    personalNoteTitle: {
      color: theme.colors.text,
    },
    personalNoteSubtitle: {
      color: theme.colors.greyBlue,
      marginTop: 2,
    },
    listContainer: {
      flex: 1,
      paddingHorizontal: theme.spacing.lg,
    },
  })
